import { tv, type VariantProps } from "tailwind-variants";

// 8-dot chasing pattern (Figma spec §5.8) — brand-600 head fading to brand-200 tail
// Rendered as SVG for crisp scaling at all sizes

const spinner = tv({
  base: "inline-block animate-spin",
  variants: {
    size: {
      xs: "size-3",   // 12px
      sm: "size-4",   // 16px
      md: "size-5",   // 20px
      lg: "size-8",   // 32px
    },
  },
  defaultVariants: { size: "md" },
});

export type SpinnerVariants = VariantProps<typeof spinner>;

export interface SpinnerProps extends SpinnerVariants {
  className?: string;
  /** Screen reader label — defaults to "Loading" */
  label?: string;
}

const DOTS = 8;

// Each dot fades opacity from 1 (index 0 = head) to 0.1 (index 7 = tail)
function getDotOpacity(index: number): number {
  return 1 - (index / DOTS) * 0.9;
}

/**
 * Spinner — 8-dot chasing pattern (Figma spec §5.8).
 * Brand-colored, no generic grey circles.
 * ADR-003: is* naming, a11y role, size variants from tokens.
 */
export function Spinner({ size, className, label = "Loading" }: SpinnerProps) {
  const radius = 38;
  const dotRadius = 6;
  const cx = 50;
  const cy = 50;

  return (
    <svg
      className={spinner({ size, className })}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="status"
      aria-label={label}
    >
      {Array.from({ length: DOTS }, (_, i) => {
        const angle = (i / DOTS) * 2 * Math.PI - Math.PI / 2;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={dotRadius}
            fill="var(--color-brand-600)"
            opacity={getDotOpacity(i)}
          />
        );
      })}
    </svg>
  );
}
